import { ABTestRules, ABTestService, ExperimentName } from '@remento/ab-testing/ab-test.types';
import { ILogger } from '@remento/logger';
import { EventEmitter } from '@remento/utils/event/EventEmitter';
import { EventMap } from '@remento/utils/event/type';
import {
  Analytics as SegmentAnalytics,
  AnalyticsBrowser as SegmentBrowserAnalytics,
  Context as SegmentEventContext,
  segmentio,
} from '@segment/analytics-next';
import { SegmentioSettings } from '@segment/analytics-next/dist/types/plugins/segmentio/index.js';
import { pascalCase } from 'change-case';
import UAParser from 'ua-parser-js';
import { v4 as uuid } from 'uuid';

import {
  AnalyticsUser,
  AnalyticsUserTraits,
  AnonymousAnalyticsUser,
  BrowserAnalyticsConfig,
  UserIdChannel,
} from './@types.js';
import { AnalyticsServiceClient } from './analytics-service-client.js';
import { IDServiceClient, LinkUserIDResponse } from './id-service-client.js';

export type EventProperties = Record<string, any>;

interface BrowserAnalyticsClientEvents<UT extends AnalyticsUserTraits = AnalyticsUserTraits> extends EventMap {
  identify: (user: AnalyticsUser<UT>, previousUser: AnalyticsUser<UT>) => void;
  page: (name: string, props: EventProperties) => void;
  track: (event: string, props: EventProperties) => void;
  reset: (user: AnonymousAnalyticsUser<UT>) => void;
}

export interface UserId {
  type: 'id';
  value: string;
}

export type UserIdentifier = UserId | UserIdChannel | Array<UserIdChannel>;

class BrowserAnalyticsSegmentPlugin {
  public readonly name = 'remento-identity';
  public readonly version = '1.0.0';
  public readonly type = 'enrichment';
  constructor(private analytics: BrowserAnalytics) {}

  private addIdentity(ctx: SegmentEventContext) {
    const user = this.analytics.getUser();
    if (user.type === 'identified') {
      ctx.event.userId = user.id;
      ctx.event.anonymousId = user.anonymousId;
    } else {
      ctx.event.anonymousId = user.id;
    }
    return ctx;
  }

  isLoaded() {
    return true;
  }

  async load() {
    return true;
  }

  track(ctx: SegmentEventContext) {
    return this.addIdentity(ctx);
  }
  page(ctx: SegmentEventContext) {
    return this.addIdentity(ctx);
  }
  identify(ctx: SegmentEventContext) {
    return this.addIdentity(ctx);
  }
}

class RemoteAnalyticsSegmentPlugin {
  public readonly name = 'remento-remote';
  public readonly version = '1.0.0';
  public readonly type = 'destination';
  constructor(private client: AnalyticsServiceClient) {}

  isLoaded() {
    return true;
  }

  async load() {
    return true;
  }

  async track(ctx: SegmentEventContext): Promise<SegmentEventContext> {
    await this.client.track(ctx.event);
    return ctx;
  }

  async page(ctx: SegmentEventContext): Promise<SegmentEventContext> {
    await this.client.page(ctx.event);
    return ctx;
  }
}

export class BrowserAnalytics<UT extends AnalyticsUserTraits = AnalyticsUserTraits> extends EventEmitter<
  BrowserAnalyticsClientEvents<UT>
> {
  public readonly segment: SegmentBrowserAnalytics;
  public readonly source: string;
  private segmentSettings: SegmentioSettings;
  private segmentEnabled: boolean;
  private init: Promise<void>;
  private readonly deviceType: 'desktop' | 'tablet' | 'mobile';

  constructor(
    private analyticsService: AnalyticsServiceClient,
    private idService: IDServiceClient,
    private abTestService: ABTestService<ABTestRules> | null,
    private logger: ILogger,
    private user: AnalyticsUser<UT>,
    config: BrowserAnalyticsConfig,
  ) {
    super();
    this.source = config.source;
    this.segmentSettings = { apiKey: config.segment.key, apiHost: `${config.segment.apiHost}/v1` };
    this.segmentEnabled = user.type != 'anonymous';
    this.segment = SegmentBrowserAnalytics.load(
      {
        writeKey: config.segment.key,
        cdnURL: config.segment.cdnURL,
      },
      {
        integrations: {
          'Segment.io': user.type == 'anonymous' ? false : this.segmentSettings,
        },
      },
    );

    const uaParser = new UAParser(navigator.userAgent);
    const uaDeviceType = uaParser.getDevice().type;
    switch (uaDeviceType) {
      case 'tablet':
        this.deviceType = 'tablet';
        break;
      case 'mobile':
      case 'wearable':
      case 'embedded':
        this.deviceType = 'mobile';
        break;
      default:
        this.deviceType = 'desktop';
        break;
    }

    this.init = this.segment
      // This plugins grabs the UserId/AnonymousID from this instance
      .register(new BrowserAnalyticsSegmentPlugin(this))
      .then(async () => {
        if (user.type === 'anonymous') {
          // Send data to the analytics server to bypass the segment backend
          this.segment
            .register(new RemoteAnalyticsSegmentPlugin(analyticsService))
            .catch((err) => this.logger.error('REGISTER_ERROR', () => ({ error: err })));
        }
        // Ensure the ids associated with the Segment client match the user provided to the constructor
        const segmentUser = await this.segment.user();
        if (user.type == 'identified') {
          const segmentUserId = segmentUser.id();
          const segmentAnonymousId = segmentUser.anonymousId();
          if (segmentUserId !== user.id || segmentAnonymousId != user.anonymousId) {
            segmentUser.id(user.id);
            segmentUser.anonymousId(user.anonymousId);
            this.logger.debug('SEGMENT_UPDATED', () => ({
              userId: user.id,
              segmentUserId: segmentUserId,
              anonymousId: user.anonymousId,
              segmentAnonymousId: segmentAnonymousId,
            }));
          }
        } else if (user.type == 'anonymous') {
          segmentUser.anonymousId(user.id);
        }
      })
      .catch((err) => {
        this.logger.error('REGISTER_ERROR', () => ({ error: err }));
        throw err;
      });
  }

  private async fetchUserId(identifier: UserIdentifier): Promise<string> {
    if (!Array.isArray(identifier) && identifier.type == 'id') {
      return identifier.value;
    }
    return await this.idService.getUserId(Array.isArray(identifier) ? identifier : [identifier]);
  }

  private async linkUserId(previousId: string, identifier: UserIdentifier): Promise<LinkUserIDResponse> {
    if (!Array.isArray(identifier) && identifier.type == 'id') {
      return { id: identifier.value, status: previousId == identifier.value ? 'UNCHANGED' : 'CONFLICT' };
    }
    return await this.idService.linkUserId(previousId, Array.isArray(identifier) ? identifier : [identifier]);
  }

  private buildUserProperties(identifier: UserIdentifier): Partial<Record<UserIdChannel['type'], string>> {
    if (Array.isArray(identifier)) {
      return identifier.reduce(
        (records, id) => Object.assign(records, this.buildUserProperties(id)),
        {} as Record<UserIdChannel['type'], string>,
      );
    }
    switch (identifier.type) {
      case 'id':
        return {};
      case 'email':
        return { email: identifier.value };
      case 'phone':
        return { phone: identifier.value };
    }
  }

  private buildExperimentsProperties(id: string): Record<string, string> {
    if (this.abTestService == null) {
      return {};
    }
    const variantsMap = this.abTestService.getAllUserVariants(id, navigator.userAgent);
    const result: Record<string, string> = {};
    for (const [experiment, variant] of Object.entries(variantsMap)) {
      result[`experiment${pascalCase(experiment)}`] = variant;
    }
    return result;
  }

  async enableSegment() {
    if (this.segmentEnabled) {
      return;
    }
    await this.init;

    this.segmentEnabled = true;

    const segmentAnalytics = await new Promise<SegmentAnalytics>((resolve, reject) => {
      this.segment.then(([analytics]) => resolve(analytics), reject);
    });
    // Enable Segment. The integrations property comes from the options provided when calling
    // SegmentBrowserAnalytics.load. If it is not updated, the plugin registered below is filtered out.
    segmentAnalytics.integrations['Segment.io'] = this.segmentSettings;
    await Promise.all([
      this.segment.register(
        segmentio(segmentAnalytics, this.segmentSettings, segmentAnalytics.settings.cdnSettings.integrations),
      ),
      this.segment.deregister('remento-remote'),
    ]);
  }

  async identify(
    identifier: UserId | UserIdChannel | Array<UserIdChannel>,
    source: string,
    properties?: UT,
    isTemporary = false,
  ) {
    if (Array.isArray(identifier) && identifier.length == 0) {
      throw new Error('Expected at least one identifier');
    }
    await this.init;

    const previousUser = this.user;
    const identifierProperties = this.buildUserProperties(identifier);

    if (previousUser.type == 'anonymous') {
      // Register the segment plugin which sends the events out and unregister the remento plugin
      const [userId] = await Promise.all([this.fetchUserId(identifier), this.enableSegment()]);
      this.user = {
        id: userId,
        anonymousId: previousUser.id,
        traits: { ...properties, ...identifierProperties } as UT,
        type: 'identified',
        source: 'new',
      };
    } else if (isTemporary || previousUser.isTemporary) {
      const userId = await this.fetchUserId(identifier);
      this.user = {
        type: 'identified',
        id: userId,
        anonymousId: null,
        traits: { ...properties, ...identifierProperties } as UT,
        isTemporary: isTemporary ?? false,
        source: 'new',
      };
    } else {
      // Attempt to link the current user to the new email
      const { id, status } = await this.linkUserId(previousUser.id, identifier);
      this.user = {
        ...previousUser,
        id: id,
        traits: { ...previousUser.traits, ...properties, ...identifierProperties },
      };
      switch (status) {
        case 'LINKED':
          this.logger.info(`IDENTIFY.LINKED`, () => ({ user: this.user, previousUser, status }));
          await this.segment.track('link-ruid', identifierProperties);
          break;
        case 'CONFLICT':
          this.logger.warn(`IDENTIFY.CONFLICT`, () => ({ user: this.user, previousUser, status }));
          await this.segment.track('link-ruid.conflict', { userId: id, ...identifierProperties });
          break;
        case 'NEW':
        case 'UNCHANGED':
          break;
      }
    }
    const experimentsProperties = this.buildExperimentsProperties(this.user.id);
    this.user.traits = {
      ...this.user.traits,
      ...experimentsProperties,
      name:
        (properties?.name ?? (properties?.firstName != null && properties?.lastName != null))
          ? `${properties.firstName} ${properties.lastName}`
          : undefined,
    };

    await this.segment.identify(this.user.id, this.user.traits);
    this.logger.info(`IDENTIFY`, () => ({ user: this.user, previousUser, status }));
    this.emit('identify', this.user, previousUser);
  }

  async reset(): Promise<void> {
    await this.init;

    if (!this.segmentEnabled) {
      return;
    }

    const newUser: AnonymousAnalyticsUser<UT> = {
      id: uuid(),
      type: 'anonymous',
      traits: {} as UT,
      source: 'new',
    };

    this.segmentEnabled = false;
    this.logger.info(`RESET`, () => ({ newUser }));

    const [segmentAnalytics] = await this.segment;
    segmentAnalytics.integrations['Segment.io'] = false;

    await Promise.all([
      this.segment.deregister('Segment.io'),
      this.segment.register(new RemoteAnalyticsSegmentPlugin(this.analyticsService)),
    ]);
    const segmentUser = await this.segment.user();
    segmentUser.id(null);
    segmentUser.anonymousId(newUser.id);
    segmentUser.traits({});
    this.user = newUser;
    this.emit('reset', newUser);
  }

  getUser(): AnalyticsUser {
    return this.user;
  }

  async page(name: string, props?: EventProperties) {
    await this.init;

    const experimentsProperties = this.buildExperimentsProperties(this.user.id);
    const properties = { ...props, ...experimentsProperties, source: this.source, deviceType: this.deviceType };
    this.logger.info(`PAGE`, () => ({ name, properties, user: this.user }));
    this.emit('page', name, properties);
    await this.segment.page(name ?? 'unknown', properties);
  }

  async track(event: string, props?: EventProperties) {
    await this.init;

    const experimentsProperties = this.buildExperimentsProperties(this.user.id);
    const properties = { ...props, ...experimentsProperties, source: this.source, deviceType: this.deviceType };
    this.logger.info(`TRACK`, () => ({ event, properties, user: this.user }));
    this.emit('track', event, properties);
    await this.segment.track(event, { ...properties, source: this.source });
  }
}
